import { useLocation } from "react-router-dom";
import { isChannelEntryInPlatform, isOnboardingPath } from "utils/routes";
import useMedia from "utils/hooks/useMedia";
import React, { ReactNode, useContext } from "react";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import { ArrowLeft, Menu, MessageCircle } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import PageTitle from "common/Home/General/PageTitle.tsx";
import {
  AFFILIATION_PATH,
  BASE_BILLING_SETTINGS_PATH,
  DASHBOARD_PATH,
  HIDE_GROUP_SELECT_PATHS,
  IS_MOBILE_WEBVIEW,
  IS_WEB_VIEW,
  MOBILE_MENU,
  MOBILE_SEARCH,
  SETTINGS_PATH,
} from "common/constants";
import NotificationsButton from "features/notifications/NotificationsButton";
import DisplayCurrencyToggle from "common/Home/Header/DisplayCurrencyToggle";
import { MobileFilters } from "common/Home/Header/MobileFilters";
import styled, { useTheme } from "styled-components";
import MobileSearch from "common/Home/Header/MobileSearch";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import UserInfo from "features/userInfo/UserInfo";
import TopSymbolsList from "features/watchlist/TopSymbols/TopSymbolsList";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import cornixHorizontalLogo from "images/svg_logo_horizontal.svg";
import cornixWhiteHorizontalLogo from "images/svg_white_logo_horizontal.svg";
import { useSelector } from "react-redux";
import { selectIsDarkMode, selectWorkspace } from "@shared/slices/common.slice";
import {
  useBottomNavigator,
  useIsHideBottomNavigatorPath,
  useIsOnboarding,
  useShowWorkspaceSwitch,
} from "utils/hooks/misc";
import { AuthUser, WorkspaceType } from "@shared/types";
import { HEADER_ICON_SIZE } from "@shared/common/Themes/constants";
import WorkspaceSwitch from "common/Home/Header/WorkspaceSwitch";
import Flex from "common/Boxes/Flex";
import {
  HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
} from "common/Home/General/constants.tsx";
import DemoTag from "common/Tags/DemoTag";
import UpgradeButton from "common/Home/Header/UpgradeButton";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import GroupSelect from "features/admin/group/GroupSelect";
import TelegramAppHeader, {
  useShowsTelegramAppHeader,
} from "common/Home/Header/TelegramAppHeader";
import { useHistory } from "react-router-dom";
import CreateBotButton from "common/Home/Header/CreateBotButton.tsx";
import PhoneChromeShadow from "common/Home/Header/PhoneChromeShadow.tsx";
import HeaderBtcTicker from "common/Home/Header/HeaderBtcTicker.tsx";
import TryNewLookButton from "features/newLook/TryNewLookButton.tsx";
import useCanToggleNewLook from "features/newLook/useCanToggleNewLook.ts";
import useMeasure from "utils/hooks/useMeasure.ts";
import { PageWrapperFiltersContext } from "common/Home/PageWrapper/PageWrapperFilters.context.ts";
import { SupportChat } from "common/Icons/Icons.tsx";
import { showIntercom } from "utils/misc.tsx";
import { useHandleNavigateBack } from "services/webview/hooks.ts";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface HeaderProps {
  toggleMobileMenu: () => void;
  user: AuthUser;
  hideMobileFilters: boolean;
  banners?: ReactNode;
}

const Header = ({
  toggleMobileMenu,
  user,
  hideMobileFilters,
  banners,
}: HeaderProps) => {
  const { useMobileFilters, useMobileSearch, searchOpen, setSearchOpen } =
    useContext(PageWrapperContext);
  const { filters } = useContext(PageWrapperFiltersContext);
  const history = useHistory();
  const isSmallScreen = useMedia([`(max-width: 1000px)`], [true], false);
  const isMobileMenu = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const isLargeMobile = useMedia(["(max-width: 750px)"], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  // In the Mini App the Telegram band above already shows the logo.
  const showsTelegramAppHeader = useShowsTelegramAppHeader();
  const isOnboarding = useIsOnboarding();
  const { isNewLook } = useTheme();
  const canToggleNewLook = useCanToggleNewLook();
  const isNewLookDesktop = isNewLook && !isBottomNavigator && !isOnboarding;
  const baseShowWorkspaceSwitch = useShowWorkspaceSwitch();
  const isSubUser = user.is_sub_user;
  const isRestrictedWhopUser = user.isRestrictedWhopUser();
  const hideIcons = isRestrictedWhopUser || isOnboarding || IS_MOBILE_WEBVIEW;
  const hideTopSymbols =
    useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false) ||
    hideIcons ||
    isSubUser ||
    isRestrictedWhopUser;
  const isDarkMode = useSelector(selectIsDarkMode);
  const workspace = useSelector(selectWorkspace);
  const isSettingsPath = useRoutesMatch(
    [SETTINGS_PATH, AFFILIATION_PATH],
    false
  );
  const isBillingPath = !!useRoutesMatch([BASE_BILLING_SETTINGS_PATH], false);
  const isDashboardPath = !!useRoutesMatch([DASHBOARD_PATH]);
  const isHideGroupSelectorPath = !!useRoutesMatch(HIDE_GROUP_SELECT_PATHS);
  const [titleMeasureRef, { width: titleWidth }] = useMeasure<HTMLDivElement>();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const [leftIconsRef, { width: leftIconsWidth }] =
    useMeasure<HTMLDivElement>();
  const [rightIconsRef, { width: rightIconsWidth }] =
    useMeasure<HTMLDivElement>();
  const freeSpace = width - titleWidth - (isNewLookDesktop ? 16 : 32);
  const isHideBottomNavigatorPath = useIsHideBottomNavigatorPath();
  const { navigateBack } = useHandleNavigateBack();
  const { isReadOnly } = useSubUserPermissions();
  const { isWhitelabel } = useBranding();

  const isDemoWorkspace = workspace === WorkspaceType.DEMO;
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const onboardingLocation = useLocation();
  // The onboarding steps carry their own heading; on a new-look phone the
  // header keeps only the logo, the demo tag and the avatar.
  const hideOnboardingTitle =
    isOnboarding &&
    !isChannelEntryInPlatform(onboardingLocation) &&
    (!isMobile || isNewLook) &&
    !isSettingsPath &&
    !isBillingPath &&
    !isAdminWorkspace;
  const showFilters =
    useMobileFilters && !hideMobileFilters && filters && !isBottomNavigator;
  const showUserInfo =
    !IS_MOBILE_WEBVIEW &&
    !isNewLookDesktop &&
    user &&
    ((user.is_admin && !user.is_onboarding && !isBottomNavigator) ||
      (!user.is_admin && (!isBottomNavigator || user.is_onboarding)));
  const isMethodWelcome =
    onboardingLocation.pathname.endsWith("/select-exchange") &&
    !onboardingLocation.search.includes("customDemo=1");
  const showDemoTag = isDemoWorkspace && isOnboarding && !isMethodWelcome;
  const showWorkspaceSwitch = baseShowWorkspaceSwitch && !isLargeMobile;
  const showGroupSelect =
    workspace === WorkspaceType.ADMIN &&
    !isMobileMenu &&
    !isOnboarding &&
    !isRestrictedWhopUser &&
    !isHideGroupSelectorPath;
  const showUpgradeButton =
    !isSmallScreen && !isOnboarding && (isDemoWorkspace || !user.isFree());
  const showBackButton =
    !isRestrictedWhopUser &&
    ((isBottomNavigator && isHideBottomNavigatorPath) ||
      (user.is_onboarding &&
        !user.isExpired() &&
        !isAdminWorkspace &&
        (isSettingsPath || isBillingPath)));

  const mobileMenuButton = (
    <LookIcon
      classic={
        <MenuOutlined
          style={{
            fontSize: HEADER_ICON_SIZE,
            marginTop: "auto",
            marginBottom: "auto",
          }}
          onClick={toggleMobileMenu}
        />
      }
      newLook={
        <Menu
          size={HEADER_ICON_SIZE}
          strokeWidth={1.75}
          style={{ marginTop: "auto", marginBottom: "auto", cursor: "pointer" }}
          onClick={toggleMobileMenu}
        />
      }
    />
  );

  const renderLeftMobileMenuButton = () => {
    if (
      !IS_MOBILE_WEBVIEW &&
      !searchOpen &&
      isMobileMenu &&
      !user.is_onboarding &&
      !isBottomNavigator &&
      !user.isRestrictedWhopUser()
    ) {
      return mobileMenuButton;
    } else if (isNewLook && isBottomNavigator && isDashboardPath) {
      // Support stays one tap away in the Menu tab's footer.
      return <HeaderBtcTicker />;
    } else if (!isWhitelabel && isBottomNavigator && isDashboardPath) {
      return (
        <LookIcon
          classic={
            <SupportChat $size={HEADER_ICON_SIZE} onClick={showIntercom} />
          }
          newLook={
            <MessageCircle
              size={HEADER_ICON_SIZE}
              strokeWidth={1.75}
              style={{ cursor: "pointer" }}
              onClick={showIntercom}
            />
          }
        />
      );
    }
  };

  const onBackButtonClick = () => {
    if (IS_WEB_VIEW) {
      navigateBack();
    } else {
      history.goBack();
    }
  };

  const renderRightAdminMenuButton = () => {
    if (user.is_admin && user.is_onboarding) {
      return mobileMenuButton;
    }
  };

  // Mini App onboarding: Telegram's band shows the logo, and a row left with
  // only the avatar would be empty space (admins keep their menu).
  const hideOnboardingRow =
    showsTelegramAppHeader &&
    user.is_onboarding &&
    !user.is_admin &&
    !showBackButton &&
    !showDemoTag &&
    isOnboardingPath(onboardingLocation);

  return (
    <HeaderWrapper $isTransparent={isNewLookDesktop}>
      <TelegramAppHeader />
      {banners}
      {/* Onboarding draws its own bar edge (its navigation bar's hairline). */}
      {isNewLook && isBottomNavigator && !isOnboarding && <PhoneChromeShadow />}
      <LayoutHeader
        $hidden={hideOnboardingRow}
        $isMobileMenu={isMobileMenu}
        $isMobile={isMobile}
        $isOnboarding={isOnboarding}
        $isNewLook={isNewLookDesktop}
      >
        <IconsWrapper ref={leftIconsRef}>
          {renderLeftMobileMenuButton()}
          {useMobileSearch && (
            <MobileSearch
              searchOpen={searchOpen}
              setSearchOpen={setSearchOpen}
            />
          )}
          {(user.is_onboarding || user.isRestrictedWhopUser()) &&
            !showBackButton &&
            !showsTelegramAppHeader && (
              <img
                src={
                  isNewLook
                    ? isDarkMode
                      ? cornixWhiteHorizontalLogo
                      : cornixHorizontalLogo
                    : isDarkMode
                    ? cornixWhiteLogo
                    : cornixLogo
                }
                style={
                  isNewLook
                    ? {
                        height: isMobile ? 22 : 28,
                        marginRight: isMobile ? 12 : 20,
                      }
                    : {
                        height: isMobile ? "60%" : "75%",
                        marginRight: isMobile ? 12 : 20,
                      }
                }
                alt="logo"
              />
            )}
          {showBackButton && (
            <LookIcon
              classic={
                <ArrowLeftOutlined
                  style={{ fontSize: 18, marginRight: 12 }}
                  onClick={onBackButtonClick}
                />
              }
              newLook={
                <ArrowLeft
                  size={20}
                  strokeWidth={1.75}
                  style={{ marginRight: 12, cursor: "pointer", flex: "none" }}
                  onClick={onBackButtonClick}
                />
              }
            />
          )}
          {showDemoTag && (
            <DemoTag
              isSmall={isMobile}
              isShort={true}
              // New look: centred on the wordmark's lowercase letters, not the
              // logo's box (the bird makes the logo taller than the word).
              style={
                isNewLook
                  ? { marginRight: 12, position: "relative", top: 4 }
                  : { marginRight: 12 }
              }
            />
          )}
        </IconsWrapper>
        {(!searchOpen || !hideTopSymbols) && (
          <TitleSymbolsWrapper ref={measureRef}>
            {!searchOpen && (
              <TitleWrapper
                ref={titleMeasureRef}
                $isOnboarding={isOnboarding}
                $leftIconsWidth={leftIconsWidth}
                $rightIconsWidth={rightIconsWidth}
              >
                {!hideOnboardingTitle && <PageTitle />}
                {showWorkspaceSwitch && (
                  <WorkspaceSwitch isShort={true} style={{ marginLeft: 16 }} />
                )}
                {showGroupSelect && (
                  <GroupSelect style={{ margin: "0 16px" }} />
                )}
              </TitleWrapper>
            )}
            {!hideTopSymbols && <TopSymbolsList maxWidth={freeSpace} />}
          </TitleSymbolsWrapper>
        )}
        <IconsWrapper ref={rightIconsRef}>
          {renderRightAdminMenuButton()}
          {canToggleNewLook &&
            !isNewLook &&
            !isBottomNavigator &&
            !isOnboarding &&
            !isSmallScreen && <TryNewLookButton />}
          {showUpgradeButton && <UpgradeButton style={{ marginLeft: 12 }} />}
          {!isOnboarding && !isReadOnly && !user?.isWhopExperience() && (
            <CreateBotButton />
          )}
          {!hideIcons && (
            <>
              <NotificationsButton iconSize={HEADER_ICON_SIZE} />
              {!isBottomNavigator && (
                <DisplayCurrencyToggle
                  style={{ marginRight: showFilters ? 0 : 12 }}
                />
              )}
            </>
          )}
          {showFilters && <MobileFilters />}
          {showUserInfo && (
            <UserInfo
              user={user}
              toggleMobileMenu={
                isBottomNavigator ? toggleMobileMenu : undefined
              }
            />
          )}
        </IconsWrapper>
      </LayoutHeader>
    </HeaderWrapper>
  );
};

export default React.memo(Header);

// Together with the new-look canvas margins (8px above and below) this keeps
// the total vertical chrome equal to HEADER_HEIGHT.
const NEW_LOOK_HEADER_HEIGHT = 64;

const HeaderWrapper = styled.div<{ $isTransparent?: boolean }>`
  height: auto;
  position: relative;
  background-color: ${(p) =>
    p.$isTransparent ? "transparent" : p.theme.colors.bgColor};
  line-height: unset;
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding: 0;
  @media (max-width: ${phoneMaxWidth}) {
    border-bottom: none;
  }
`;
export const LayoutHeader = styled.div<{
  $isMobile?: boolean;
  $isOnboarding?: boolean;
  $isMobileMenu?: boolean;
  $isNewLook?: boolean;
  $hidden?: boolean;
}>`
  ${(p) => p.$hidden && "display: none !important;"}
  position: relative;
  background-color: ${(p) =>
    p.$isNewLook ? "transparent" : p.theme.colors.bgColor};
  height: ${(p) =>
    p.$isMobile
      ? MOBILE_HEADER_HEIGHT
      : p.$isNewLook
      ? NEW_LOOK_HEADER_HEIGHT
      : HEADER_HEIGHT}px;
  padding: 0 ${(p) => (p.$isMobileMenu ? "16px" : "20px")};
  display: flex;
  justify-content: ${(p) =>
    p.$isMobileMenu || p.$isOnboarding ? "space-between" : "unset"};
  line-height: unset;
  @media (max-width: ${phoneMaxWidth}) {
    /* The onboarding's phone gutter is 20px (ONBOARDING_PHONE_GUTTER). */
    padding: 0 ${(p) => (!p.theme.isNewLook ? 12 : p.$isOnboarding ? 20 : 16)}px;
    height: 48px;
  }
`;
const IconsWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: ${(p) => p.theme.colors.secondaryText};
  height: 100%;
`;
const TitleWrapper = styled(Flex)<{
  $isOnboarding: boolean;
  $leftIconsWidth: number;
  $rightIconsWidth: number;
}>`
  justify-content: flex-start;
  margin-top: auto;
  margin-bottom: auto;
  overflow: hidden;
  width: fit-content;
  @media (max-width: ${phoneMaxWidth}) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: calc(
      100% -
        max(
          ${(p) => p.$leftIconsWidth + (p.theme.isNewLook ? 24 : 8)}px,
          ${(p) => p.$rightIconsWidth + (p.theme.isNewLook ? 24 : 8)}px
        ) * 2
    );
    ${(p) =>
      p.$isOnboarding &&
      `
      position: static;
      transform: none;
      width: 100%;
      min-width: 0;
    `}
  }
`;
const TitleSymbolsWrapper = styled(Flex)`
  flex: 1;
  min-width: 0;
  width: 100%;
`;
